import type { ReactNode } from 'react';
import { Button } from '@react-apps/design-system';

/**
 * Ação de ícone. O `label` é obrigatório **na assinatura**, e é ele que
 * preenche `aria-label` e `title` (tooltip nativo — o design-system não tem
 * `Tooltip`, e não vale inventar um para isto).
 *
 * Obrigatório porque um botão cujo conteúdo é só um `<svg aria-hidden>` é um
 * botão **sem nome acessível**: o leitor de tela anuncia "botão" e nada mais.
 * Exigir o rótulo no tipo é o que impede a próxima ação em ícone de nascer muda.
 *
 * Ícone sozinho serve para ação **repetida numa lista**, onde o contexto está no
 * card. Ação única e consequente (publicar a prova, excluir a seleção, anular
 * uma questão) continua escrita.
 */
export function IconButton({
  label,
  icon,
  onClick,
  variant = 'ghost',
  disabled,
  blockedBy,
  busy = false,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  variant?: 'ghost' | 'secondary' | 'danger' | 'primary';
  disabled?: boolean;
  /**
   * Bloqueado, mas **focável**: o `id` do texto que diz por quê. Vira
   * `aria-disabled` + `aria-describedby`, e o clique não faz nada. O
   * `disabled` tiraria o botão do foco, e o leitor de tela nunca chegaria ao
   * motivo.
   */
  blockedBy?: string;
  /**
   * Pedido no ar (o lock que o próprio clique pediu): também focável e sem
   * clique, com `aria-busy`. O `disabled` tiraria o foco do botão que a pessoa
   * acabou de apertar, e ele cairia no `body`.
   */
  busy?: boolean;
}) {
  const blocked = blockedBy !== undefined || busy;
  return (
    <Button
      type="button"
      variant={variant}
      size="xs"
      onClick={blocked ? undefined : onClick}
      disabled={disabled}
      aria-disabled={blocked || undefined}
      aria-busy={busy || undefined}
      aria-describedby={blockedBy}
      aria-label={label}
      title={label}
      className={blocked ? 'px-1.5 py-1.5 opacity-50' : 'px-1.5 py-1.5'}
    >
      {icon}
    </Button>
  );
}
